import type { QueryClient } from '@tanstack/react-query';
import { apiClient } from '@/api/client';
import { useDialogStore } from '@/stores/dialog-store';
import { useModalStore } from '@/stores/modal-store';
import { useToastStore } from '@/stores/toast-store';
import type { WorkareaTab } from '@/types';
import type { DatabaseActionDeps } from './database-actions';

interface BuildDeps {
  connectionId: string;
  dbName: string;
  defaultSchema: string;
  openTab: (tab: WorkareaTab) => void;
  queryClient: QueryClient;
  /** Callback that opens the restore-from-SQL file picker. Both ConnectionTree
   *  and InfoView route this through their parent because it needs <input
   *  type="file"> that lives in the sidebar. */
  onTriggerRestore: (connectionId: string) => void;
  /** Opens the ImportModal with initial state. Currently owned by ConnectionTree;
   *  InfoView-from-database falls back to a toast until ImportModal moves to
   *  useModalStore. */
  onShowImportModal?: (state: {
    connectionId: string;
    schema: string;
    tableName?: string;
    tableColumns?: { name: string; type: string }[];
  }) => void;
}

/**
 * Assemble DatabaseActionDeps by wiring each action to the appropriate
 * store/mutation/callback. Callable from any component that needs the
 * database action list (ConnectionTree context menu, InfoView Actions
 * panel, etc.). Keeps the single source of truth intact.
 */
export function getDatabaseActionDeps(opts: BuildDeps): DatabaseActionDeps {
  const { connectionId, dbName, defaultSchema, openTab, queryClient } = opts;

  const downloadSQL = (filename: string, sql: string) => {
    const blob = new Blob([sql], { type: 'text/sql' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = filename;
    a.click();
    URL.revokeObjectURL(url);
  };

  return {
    onCreateTable: () => {
      openTab({
        type: 'designer',
        id: `designer:${connectionId}:${dbName}:${defaultSchema}:new:${Date.now()}`,
        connectionId,
        database: dbName,
        schema: defaultSchema,
        mode: 'create',
      });
    },
    onCreateView: () => {
      openTab({
        type: 'code-editor',
        id: `code-editor:${connectionId}:${dbName}:${defaultSchema}:new-view:${Date.now()}`,
        connectionId,
        database: dbName,
        schema: defaultSchema,
        objectName: 'new_view',
        objectType: 'view',
        mode: 'create',
      });
    },
    onCreateFunction: () => {
      openTab({
        type: 'code-editor',
        id: `code-editor:${connectionId}:${dbName}:${defaultSchema}:new-func:${Date.now()}`,
        connectionId,
        database: dbName,
        schema: defaultSchema,
        objectName: 'new_function',
        objectType: 'function',
        mode: 'create',
      });
    },
    onViewERD: () => {
      openTab({
        type: 'erd',
        id: `erd:${connectionId}:${dbName}`,
        connectionId,
        databaseName: dbName,
      });
    },
    onReload: async () => {
      // Force a bypass-cache fetch (?refresh=1) so the server hits the live
      // DB and rewrites its cache row. Then seed TanStack Query with the
      // result so the tree re-renders immediately with fresh metadata.
      try {
        const fresh = await apiClient.get(
          `/api/schema/${connectionId}?database=${encodeURIComponent(dbName)}&refresh=1`,
        );
        queryClient.setQueryData(['schema', connectionId, dbName], fresh);
      } catch (err: unknown) {
        useToastStore.getState().addToast(
          'error',
          err instanceof Error ? err.message : 'Reload failed',
        );
      }
      queryClient.invalidateQueries({ queryKey: ['schema-stats', connectionId, dbName] });
    },
    onBackup: () => {
      apiClient
        .post<{ sql: string; tableCount: number; totalRows: number }>(
          `/api/management/${connectionId}/backup-database`,
          { database: dbName },
        )
        .then((result) => {
          downloadSQL(`${dbName}_backup.sql`, result.sql);
          useToastStore.getState().addToast('success', 'Backup downloaded');
        })
        .catch((err: unknown) => {
          const msg = err instanceof Error ? err.message : 'Backup failed';
          useToastStore.getState().addToast('error', msg);
        });
    },
    onRestore: () => {
      opts.onTriggerRestore(connectionId);
    },
    onImport: () => {
      if (opts.onShowImportModal) {
        opts.onShowImportModal({ connectionId, schema: defaultSchema });
      } else {
        useToastStore.getState().addToast('info', 'Import is available from the sidebar for now.');
      }
    },
    onEdit: () => {
      useModalStore.getState().openEditDatabase(connectionId, dbName);
    },
    onDrop: () => {
      useDialogStore.getState().showConfirm({
        title: 'Drop Database',
        message: `This will permanently delete the database "${dbName}" and all its data. This action cannot be undone.`,
        confirmText: dbName,
        danger: true,
        onConfirm: async () => {
          // Drop-database is routed via the connection's default pool (the
          // DB being dropped can't drop itself) — no `database` param.
          await apiClient.post(`/api/management/${connectionId}/drop-database`, {
            name: dbName,
            confirmName: dbName,
          });
          queryClient.invalidateQueries({ queryKey: ['databases', connectionId] });
          queryClient.invalidateQueries({ queryKey: ['schema', connectionId, dbName] });
          useToastStore.getState().addToast('success', `Dropped database ${dbName}`);
        },
      });
    },
  };
}
